// i18next-extract-mark-ns-start homepage
import React from 'react';
import styled from 'styled-components';
import { useTranslation } from 'gatsby-plugin-react-i18next';
import { TFunction } from '../../../types/t-types';
import routesMap from '../../../Routes';
import 'react-responsive-carousel/lib/styles/carousel.min.css';
import withAppContext from '../../../withAppContext';
import withNavigation from '../../../hoc/withNavigation';
import { navigate } from '../../../services/navigation';
import {
  tabletThresholdPixels, frames, colors, margins, mobileThresholdPixels,
} from '../v4/styledComponents';
import star from '../../../assets/feedbacks/star.png';
import person from '../../../assets/feedbacks/person.svg';
import feedbackImg from '../../../assets/feedbacks/feedback.webp';
import { ratingData } from '../v3/ratingData';
import './HomeCarousel.css';
import { slashNtoBr } from '../../../services/formatting';

const Container = styled.div<{ marginTop?: boolean }>`
  max-width: 1180px;
  width: 100%;
  margin: ${(props) => (props.marginTop ? '120px' : '0')} ${frames.desktop} ${margins.m15};
  display: flex;
  justify-content: space-between;
  gap: 69px;

  @media (max-width: ${tabletThresholdPixels}) {
    margin: ${margins.m11} ${frames.tablet} ;
    gap: unset;
    width: calc(100vw - (${frames.tablet}*2));
  }
  @media (max-width: ${mobileThresholdPixels}) {
    margin: ${margins.m11} ${frames.mobile};
    width: calc(100vw - (${frames.mobile}*2));
  }
`;
const Content = styled.div`
  overflow: hidden;
`;
const Title = styled.h2<{ big?: boolean }>`
  text-transform: uppercase;
  font-size: 70px;
  font-weight: 700;
  margin: 0;
  @media (max-width: ${tabletThresholdPixels}) {
    font-size: 48px;
  }
  @media (max-width: ${mobileThresholdPixels}) {
    font-size: ${({ big }) => big ? '32px' : '28px'};
  }
`;
const NoteContainer = styled.div`
  display: flex;
  margin-top: 24px;
`;
const Note = styled.p`
  font-size: 20px;
  font-weight: 700;
  margin: 0;
  @media (max-width: ${tabletThresholdPixels}) {
    padding-right: 10px;
  }
`;
const Scroll = styled.div`
  overflow-x: scroll;
  scrollbar-width: none;
  ::-webkit-scrollbar {
    display: none;
  }
`;
const SubContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${margins.m3};
  margin-top: ${margins.m4};
  @media (max-width: ${mobileThresholdPixels}) {
    margin-top: ${margins.m3};
    flex-direction: row;
    width: 200%;
  }
`;
const BlockContainer = styled.div`
  max-width: 392px;
  display: flex;
  gap: 16px;
  align-items: flex-start;
  padding: 0 24px;
  @media (max-width: ${tabletThresholdPixels}) {
    padding: 0;
    width: 286px;
    gap: 1rem;
  }
`;
const Name = styled.p`
  font-size: 14px;
  margin: 5px 0 0;
  color: ${colors.grey600};
`;
const FeedbackText = styled.p`
  font-size: 14px;
  margin: 0;
`;
const Stars = styled.img`
  height: 20px;
  margin-top: 14px;
  @media (max-width: ${tabletThresholdPixels}) {
    margin-top: 0;
  }
`;
const FeedbacksLink = styled.div`
  font-size: 14px;
  text-decoration: underline;
  text-transform: uppercase;
  margin-top: ${margins.m4};
  cursor: pointer;
  @media (max-width: ${tabletThresholdPixels}) {
    margin-top: ${margins.m3};
  }
`;
const ButtonContainer = styled.div`
  margin-top: 40px;
`;
const Img = styled.img`
  border-radius: 4px;
  max-width: 542px;
  max-height: 672px;
    @media (max-width: ${tabletThresholdPixels}) {
    max-height: 480px;
    width: auto;
    object-fit: contain;
  }
    @media (max-width: ${mobileThresholdPixels}) {
    max-width: 345px;
  }
`;
const Icon = styled.img`
  width: 32px;
  height: 32px;
`;

type FeedbacksType = {
  name: string;
  feedback: string;
}[];

const feedbacks = (t: TFunction, position: string): FeedbacksType => [
  {
    name: t('Alice P'),
    feedback: t(
      // eslint-disable-next-line max-len
      '“Derrière l‘expérience utilisateur du site qui est très efficace, il y a un service humain exceptionnel. J‘ai été agréablement surprise par le côté professionnel et la qualité de la prestation. La couturière est en plus adorable. Je recommande à 100%.”',
    ),
  },
  {
    name: t('Victoria C'),
    feedback: t('“Rapide, efficace, beau travail pour un prix raisonnable à {{position}}. Je recommande!”', { position }),
  },
  {
    name: t('Eric L'),
    feedback: t(
      // eslint-disable-next-line
      '“Quand d‘autres disaient "ce que vous demandez est infaisable !", Tilli l‘a fait et bien fait. Quand l‘expertise technique est combinée à un accueil chaleureux, l‘expérience est très agréable.”',
    ),
  },
];

type FeedbacksProps = {
  context: {
    isMobile: boolean;
  };
  button: React.ReactNode;
  marginTop: boolean;
  position: string;
};

function Feedbacks({
  context: { isMobile }, button, marginTop, position,
}: FeedbacksProps) {
  const { t } = useTranslation();
  const numStars = 5;
  const stars: any = [];

  Array.from({ length: numStars }, (_, i) => (
    stars.push(<Stars src={star} alt={t('étoiles')} key={i} />)
  ));

  const currentPosition = position || 'Paris';

  return (
    <Container marginTop={marginTop}>
      <Content>
        {!isMobile ? (
          <>
            <Note>{`${ratingData.scoring}/10`}</Note>
            {stars}
            <Title>{`${Number(ratingData.numberOfReviews).toLocaleString('fr-FR')} ${t('AVIS')}`}</Title>
          </>
        ) : (
          <>
            <Title big>{slashNtoBr(t('Les avis de \nnos clients'))}</Title>
            <NoteContainer>
              <Note>{`${ratingData.scoring}/10`}</Note>
              {stars}
            </NoteContainer>
            <Title>{`${Number(ratingData.numberOfReviews).toLocaleString('fr-FR')} ${t('AVIS')}`}</Title>
          </>
        )}
        <Scroll>
          <SubContainer>
            {feedbacks(t, currentPosition).map((feedback) => (
              <BlockContainer key={feedback.name}>
                {!isMobile && <Icon src={person} alt={t('avis des clients')} />}
                <div>
                  <FeedbackText>{feedback.feedback}</FeedbackText>
                  <Name>{feedback.name}</Name>
                </div>
              </BlockContainer>
            ))}
          </SubContainer>
        </Scroll>
        <FeedbacksLink onClick={() => navigate(routesMap.CustomerFeedbacksPage.url)}>
          {t('Voir tous nos avis')}
        </FeedbacksLink>
        {button && (
          <ButtonContainer>
            {button}
          </ButtonContainer>
        )}
      </Content>
      {!isMobile && <Img src={feedbackImg} alt={t('avis des clients')} />}
    </Container>
  );
}

export default withAppContext(withNavigation(Feedbacks));
